/**
 * CVE Analysis Panel
 * Displays AI-powered in-depth analysis of a CVE
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { AIRiskBadge } from './AIRiskBadge';
import { getApiUrl } from '../config/api';

interface CVEAnalysisPanelProps {
  cveId: string;
  className?: string;
}

interface CVEAnalysis {
  cve_id: string;
  ai_analysis: {
    risk_score: number;
    urgency_level: 'critical' | 'high' | 'medium' | 'low';
    severity_justification: string;
    real_world_impact: string;
    exploit_likelihood: string;
    detailed_analysis: string;
    deployment_recommendation: string;
  } | null;
  technical_details: {
    exploitability_assessment: string;
    attack_vector: string;
    attack_complexity: string;
    privileges_required: string;
  } | null;
  workarounds: string[];
  mitigation_recommendations: string[];
}

export function CVEAnalysisPanel({ cveId, className = '' }: CVEAnalysisPanelProps) {
  const { getToken } = useAuth();
  const isAdmin = true; // TODO: Replace with actual auth check
  const [analysis, setAnalysis] = useState<CVEAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexSuccess, setReindexSuccess] = useState(false);

  const loadAnalysis = () => {
    setLoading(true);
    setError(null);
    fetch(getApiUrl(`api/v1/ai/cve-analysis/${cveId}`))
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAnalysis(data.data);
        } else {
          setError(data.error || 'Failed to load analysis');
        }
        setLoading(false);
      })
      .catch((err) => {
        setError('Network error: ' + err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadAnalysis();
  }, [cveId]);

  const handleReindex = async () => {
    setReindexing(true);
    setReindexSuccess(false);

    try {
      const token = await getToken();

      if (!token) {
        alert('Authentication required. Please sign in.');
        setReindexing(false);
        return;
      }

      const res = await fetch(getApiUrl(`api/v1/ai/reindex-cve/${cveId}`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error:', res.status, errorText);
        alert(`Re-indexing failed: ${res.status} ${res.statusText}\n${errorText}`);
        setReindexing(false);
        return;
      }

      const data = await res.json();

      if (data.success) {
        setReindexSuccess(true);
        alert(`Success! CVE ${cveId} queued for AI analysis. This may take a few minutes.`);
        setTimeout(() => {
          setReindexSuccess(false);
          loadAnalysis(); // Reload after a delay
        }, 5000);
      } else {
        alert(`Re-indexing failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Re-indexing error:', err);
      alert(`Error: ${String(err)}`);
    } finally {
      setReindexing(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-gray-800/30 rounded-lg border border-gray-700 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyber-blue-500"></div>
          <span>Loading AI analysis...</span>
        </div>
      </div>
    );
  }

  if (error || !analysis?.ai_analysis) {
    return (
      <div className={`bg-gray-800/20 rounded-lg border border-gray-700/50 p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>⏳</span>
            <span>AI analysis pending - check back soon</span>
          </div>
          {isAdmin && (
            <button
              onClick={handleReindex}
              disabled={reindexing}
              className="px-3 py-1 text-xs bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              title="Admin: Trigger AI analysis"
            >
              {reindexing ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyber-blue-400"></div>
                  <span>Analyzing...</span>
                </>
              ) : reindexSuccess ? (
                <>
                  <span>✓</span>
                  <span>Queued</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-3 h-3" />
                  <span>Analyze</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  const { ai_analysis, technical_details, workarounds, mitigation_recommendations } = analysis;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* AI Analysis Header */}
      <div className="flex items-center justify-between bg-cyber-blue-500/10 border border-cyber-blue-500/30 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">🤖</span>
          <span className="font-semibold text-cyber-blue-400">AI-Powered Analysis</span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={handleReindex}
              disabled={reindexing}
              className="px-3 py-1 text-xs bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
              title="Admin: Trigger AI re-analysis"
            >
              {reindexing ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyber-blue-400"></div>
                  <span>Re-analyzing...</span>
                </>
              ) : reindexSuccess ? (
                <>
                  <span>✓</span>
                  <span>Queued</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-3 h-3" />
                  <span>Re-analyze</span>
                </>
              )}
            </button>
          )}
          <AIRiskBadge score={ai_analysis.risk_score} urgencyLevel={ai_analysis.urgency_level} />
        </div>
      </div>

      {/* Severity Justification */}
      <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <ShieldExclamationIcon className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-orange-400 mb-2 uppercase">
              Why This Severity?
            </h4>
            <p className="text-sm text-gray-300 leading-relaxed">
              {ai_analysis.severity_justification}
            </p>
          </div>
        </div>
      </div>

      {/* Real-World Impact */}
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-red-400 mb-2 uppercase">
              Real-World Impact
            </h4>
            <p className="text-sm text-red-100 leading-relaxed">{ai_analysis.real_world_impact}</p>
          </div>
        </div>
      </div>

      {/* Exploit Likelihood */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <LightBulbIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-yellow-400 mb-2 uppercase">
              Exploit Likelihood
            </h4>
            <p className="text-sm text-yellow-100 leading-relaxed">
              {ai_analysis.exploit_likelihood}
            </p>
            {ai_analysis.exploit_likelihood.toLowerCase().includes('active') && (
              <div className="mt-3 bg-red-900/30 border border-red-500/50 rounded px-3 py-2">
                <p className="text-sm text-red-200 font-semibold">
                  ⚠️ Active exploitation detected! Immediate action required.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deployment Recommendation */}
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <WrenchScrewdriverIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-green-400 mb-2 uppercase">
              Recommended Action
            </h4>
            <p className="text-sm text-green-100 font-medium">
              {ai_analysis.deployment_recommendation}
            </p>
          </div>
        </div>
      </div>

      {/* Technical Details (if available) */}
      {technical_details && (
        <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-3">Technical Details</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Attack Vector:</span>
              <span className="ml-2 text-gray-200 capitalize">
                {technical_details.attack_vector}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Attack Complexity:</span>
              <span className="ml-2 text-gray-200 capitalize">
                {technical_details.attack_complexity}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500">Privileges Required:</span>
              <span className="ml-2 text-gray-200 capitalize">
                {technical_details.privileges_required}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Workarounds (if available) */}
      {workarounds && workarounds.length > 0 && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-400 mb-3">Workarounds</h4>
          <ul className="space-y-2 text-sm text-blue-100">
            {workarounds.map((workaround, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">•</span>
                <span>{workaround}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mitigation Recommendations (if available) */}
      {mitigation_recommendations && mitigation_recommendations.length > 0 && (
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-400 mb-3">Mitigation Steps</h4>
          <ol className="space-y-2 text-sm text-purple-100">
            {mitigation_recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-purple-400 font-semibold">{index + 1}.</span>
                <span>{recommendation}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Detailed Analysis */}
      {ai_analysis.detailed_analysis && (
        <details className="bg-gray-800/30 rounded-lg border border-gray-700">
          <summary className="cursor-pointer p-4 hover:bg-gray-800/50 transition-colors font-medium text-sm text-gray-300">
            View Full AI Analysis
          </summary>
          <div className="px-4 pb-4 text-sm text-gray-300 leading-relaxed border-t border-gray-700 pt-4">
            {ai_analysis.detailed_analysis}
          </div>
        </details>
      )}
    </div>
  );
}
